/**
 * Provider Credentials admin sayfası.
 *
 * Restoran sahibinin PAX/Verifone/Iyzico/SMS/GİB vendor API key'lerini
 * yönettiği sayfa. Dinamik form: seçilen provider'a göre alanlar
 * /api/v1/providers/ registry'sinden gelir.
 */
import { Card, Stack, Table, Badge, Loader, Center, Button, Group, ActionIcon, Modal, Select, TextInput, PasswordInput, Switch, Text, Alert, Code, Tooltip } from "@mantine/core";
import { IconPlus, IconEdit, IconTrash, IconRefresh, IconCheck, IconAlertCircle, IconMessage } from "@tabler/icons-react";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useResource, useRemove, apiClient } from "../../../shared/api";
import { PageHeader, EmptyState, formatDateTime, confirmDelete, validatePhoneTR } from "../../../shared/ui";

interface ProviderField {
  name: string;
  label: string;
  field_type: "text" | "password" | "url" | "number" | "select";
  required: boolean;
  is_secret: boolean;
  placeholder?: string;
  help_text?: string;
  choices?: string[];
}

interface ProviderSpec {
  key: string;
  name: string;
  category: string;
  description: string;
  docs_url: string;
  sandbox_supported: boolean;
  fields: ProviderField[];
}

const CATEGORY_LABEL: Record<string, string> = {
  PAYMENT_TERMINAL: "💳 POS Terminali",
  ONLINE_PAYMENT: "🌐 Online Ödeme",
  SMS: "📱 SMS",
  INVOICE: "🧾 E-Fatura",
  DELIVERY: "🛵 Delivery",
};

const CATEGORY_COLOR: Record<string, string> = {
  PAYMENT_TERMINAL: "blue",
  ONLINE_PAYMENT: "green",
  SMS: "violet",
  INVOICE: "orange",
  DELIVERY: "red",
};

export function ProviderCredentialsPage() {
  return (
    <Stack gap="md">
      <PageHeader
        title="3rd Party Entegrasyonlar"
        subtitle="PAX, Verifone, Iyzico, NetGSM, GİB e-Fatura vendor API anahtarları"
      />
      <Alert color="orange" icon={<IconAlertCircle size={18} />}>
        ⚠️ Buradaki bilgiler <strong>encrypted</strong> saklanır ve <strong>HIGH severity audit log</strong>'a yazılır.
        Şifreleri sadece tenant OWNER görebilir.
      </Alert>

      <CredentialsList />
    </Stack>
  );
}

function CredentialsList() {
  const { data, isLoading, refetch } = useResource<any>("provider-credentials");
  const remove = useRemove("provider-credentials");
  const [opened, { open, close }] = useDisclosure(false);
  const [editing, setEditing] = useState<any | null>(null);
  // Test SMS modal — window.prompt() yerine düzgün form
  const [smsTarget, setSmsTarget] = useState<{ id: string } | null>(null);
  const [smsPhone, setSmsPhone] = useState("");
  const [smsSending, setSmsSending] = useState(false);

  const list: any[] = data?.results || data || [];
  if (isLoading) return <Center h={200}><Loader /></Center>;

  async function handleDelete(id: string) {
    if (!(await confirmDelete(
      "Credential silinsin mi? Bu cihazlara bağlı ise çalışmayı durdurabilir.",
    ))) return;
    await remove.mutateAsync(id);
    notifications.show({ message: "Silindi", color: "green" });
  }

  async function handleTest(id: string) {
    try {
      const { data } = await apiClient.post(`/api/v1/provider-credentials/${id}/test-connection/`);
      const msg = data.success
        ? data.balance
          ? `Bağlantı OK ✓ — Kalan kontör: ${data.balance}`
          : "Bağlantı OK ✓"
        : `Hata: ${data.error}`;
      notifications.show({
        message: msg,
        color: data.success ? "green" : "red",
        autoClose: data.success ? 5000 : 8000,
      });
      refetch();
    } catch (e: any) {
      notifications.show({ message: e?.response?.data?.detail || "Test başarısız", color: "red" });
    }
  }

  function handleSendTestSms(id: string) {
    setSmsPhone("");
    setSmsTarget({ id });
  }

  async function submitTestSms() {
    if (!smsTarget) return;
    const phone = smsPhone.trim();
    if (!phone) return;
    const phoneErr = validatePhoneTR(phone);
    if (phoneErr) {
      notifications.show({ message: phoneErr, color: "red" });
      return;
    }
    setSmsSending(true);
    try {
      const { data } = await apiClient.post(
        `/api/v1/provider-credentials/${smsTarget.id}/send-test-sms/`,
        { phone },
      );
      notifications.show({
        message: data.success
          ? `Test SMS gönderildi ✓ (${data.provider}, id: ${data.bulk_id || "—"})`
          : `Hata: ${data.error || "bilinmiyor"}`,
        color: data.success ? "green" : "red",
        autoClose: 6000,
      });
      if (data.success) setSmsTarget(null);
    } catch (e: any) {
      notifications.show({
        message: e?.response?.data?.detail || e?.response?.data?.error || "Gönderilemedi",
        color: "red",
      });
    } finally {
      setSmsSending(false);
    }
  }

  return (
    <Stack gap="sm">
      <Group justify="space-between">
        <Button leftSection={<IconRefresh size={16} />} variant="default" onClick={() => refetch()}>Yenile</Button>
        <Button leftSection={<IconPlus size={16} />} onClick={() => { setEditing(null); open(); }}>Yeni Credential</Button>
      </Group>

      {list.length === 0 ? (
        <EmptyState
          title="Henüz credential yok"
          description="PAX A920 banka MID/TID, Iyzico API key, NetGSM şifresi gibi vendor bilgilerini buraya ekleyin. Cihaz tarafına şifreli aktarılır."
        />
      ) : (
        <Card padding="sm" radius="md" withBorder>
          <Table.ScrollContainer minWidth={820}>
          <Table highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Ad</Table.Th>
                <Table.Th>Provider</Table.Th>
                <Table.Th>Kategori</Table.Th>
                <Table.Th>Şube</Table.Th>
                <Table.Th>Ortam</Table.Th>
                <Table.Th>Son Test</Table.Th>
                <Table.Th>Durum</Table.Th>
                <Table.Th>İşlem</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {list.map((c: any) => (
                <Table.Tr key={c.id}>
                  <Table.Td fw={500}>{c.name}</Table.Td>
                  <Table.Td><Badge variant="light">{c.provider_name}</Badge></Table.Td>
                  <Table.Td><Badge color={CATEGORY_COLOR[c.category] || "gray"}>{CATEGORY_LABEL[c.category] || c.category}</Badge></Table.Td>
                  <Table.Td>{c.branch_name || "Tüm Şubeler"}</Table.Td>
                  <Table.Td>{c.is_sandbox ? <Badge color="yellow">Sandbox</Badge> : <Badge color="red">CANLI</Badge>}</Table.Td>
                  <Table.Td>{c.last_test_at ? formatDateTime(c.last_test_at) : "-"}</Table.Td>
                  <Table.Td>
                    {c.last_test_success === true && <Badge color="green">✓ OK</Badge>}
                    {c.last_test_success === false && <Badge color="red">✗ Hata</Badge>}
                    {c.last_test_success === null && <Badge color="gray">—</Badge>}
                  </Table.Td>
                  <Table.Td>
                    <Group gap={4}>
                      <Tooltip label="Bağlantıyı test et (ücretsiz)">
                        <ActionIcon variant="subtle" color="blue" onClick={() => handleTest(c.id)}><IconCheck size={16} /></ActionIcon>
                      </Tooltip>
                      {c.provider_key === "NETGSM" && (
                        <Tooltip label="Test SMS gönder (1 kontör tüketir)">
                          <ActionIcon variant="subtle" color="grape" onClick={() => handleSendTestSms(c.id)}><IconMessage size={16} /></ActionIcon>
                        </Tooltip>
                      )}
                      <ActionIcon variant="subtle" onClick={() => { setEditing(c); open(); }}><IconEdit size={16} /></ActionIcon>
                      <ActionIcon variant="subtle" color="red" onClick={() => handleDelete(c.id)}><IconTrash size={16} /></ActionIcon>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
          </Table.ScrollContainer>
        </Card>
      )}

      <CredentialModal opened={opened} onClose={close} editing={editing} />

      {/* Test SMS modal — window.prompt() yerine */}
      <Modal
        opened={!!smsTarget}
        onClose={() => !smsSending && setSmsTarget(null)}
        title="Test SMS Gönder"
        size="sm"
        centered
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Format: <Code>+905...</Code> veya <Code>05...</Code>
          </Text>
          <Alert color="yellow" icon={<IconAlertCircle size={16} />}>
            1 SMS kontörü tüketilir, gerçek mesaj gider.
          </Alert>
          <TextInput
            label="Hedef numara"
            required
            placeholder="+905551234567"
            value={smsPhone}
            onChange={(e) => setSmsPhone(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && smsPhone.trim()) submitTestSms();
            }}
            autoFocus
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setSmsTarget(null)} disabled={smsSending}>
              Vazgeç
            </Button>
            <Button
              color="green"
              leftSection={<IconMessage size={16} />}
              onClick={submitTestSms}
              loading={smsSending}
              disabled={!smsPhone.trim()}
            >
              Gönder
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}

function CredentialModal({ opened, onClose, editing }: { opened: boolean; onClose: () => void; editing: any | null }) {
  const qc = useQueryClient();
  const { data: providers } = useQuery<ProviderSpec[]>({
    queryKey: ["providers-registry"],
    queryFn: async () => (await apiClient.get("/api/v1/providers/")).data,
    enabled: opened,
  });
  const { data: branchesData } = useResource<any>("branches", { enabled: opened });
  const branches: any[] = branchesData?.results || branchesData || [];

  const [providerKey, setProviderKey] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [branchId, setBranchId] = useState<string | null>(null);
  const [isSandbox, setIsSandbox] = useState(true);
  const [isActive, setIsActive] = useState(true);
  const [fields, setFields] = useState<Record<string, string>>({});

  useEffect(() => {
    if (editing) {
      setProviderKey(editing.provider_key);
      setName(editing.name);
      setBranchId(editing.branch);
      setIsSandbox(editing.is_sandbox);
      setIsActive(editing.is_active);
      setFields(editing.masked_fields || {});
    } else {
      setProviderKey(null);
      setName("");
      setBranchId(null);
      setIsSandbox(true);
      setIsActive(true);
      setFields({});
    }
  }, [editing, opened]);

  const selectedSpec = providers?.find((p) => p.key === providerKey);

  const mutation = useMutation({
    mutationFn: async () => {
      const payload = {
        name,
        provider_key: providerKey,
        branch: branchId,
        is_sandbox: isSandbox,
        is_active: isActive,
        fields,
      };
      if (editing) {
        return (await apiClient.patch(`/api/v1/provider-credentials/${editing.id}/`, payload)).data;
      }
      return (await apiClient.post("/api/v1/provider-credentials/", payload)).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["provider-credentials"] });
      notifications.show({ message: editing ? "Güncellendi" : "Oluşturuldu", color: "green" });
      onClose();
    },
    onError: (e: any) => notifications.show({
      message: e?.response?.data?.detail || JSON.stringify(e?.response?.data || "Hata"),
      color: "red",
    }),
  });

  function renderField(field: ProviderField) {
    const value = fields[field.name] || "";

    if (field.field_type === "select") {
      return (
        <Select
          key={field.name}
          label={field.label}
          required={field.required}
          data={field.choices || []}
          value={value}
          onChange={(v) => setFields({ ...fields, [field.name]: v || "" })}
          description={field.help_text}
        />
      );
    }
    if (field.field_type === "password" || field.is_secret) {
      return (
        <PasswordInput
          key={field.name}
          label={field.label}
          required={field.required && !editing}
          placeholder={field.placeholder || (editing ? "Değiştirmek için yeni değer girin" : "")}
          value={value}
          onChange={(e) => setFields({ ...fields, [field.name]: e.currentTarget.value })}
          description={field.help_text || (field.is_secret ? "🔒 Encrypt edilerek saklanır" : undefined)}
        />
      );
    }
    return (
      <TextInput
        key={field.name}
        type={field.field_type === "number" ? "number" : "text"}
        label={field.label}
        required={field.required}
        placeholder={field.placeholder}
        value={value}
        onChange={(e) => setFields({ ...fields, [field.name]: e.currentTarget.value })}
        description={field.help_text}
      />
    );
  }

  return (
    <Modal opened={opened} onClose={onClose} title={editing ? "Credential Düzenle" : "Yeni Vendor Credential"} size="lg">
      <Stack gap="md">
        <Select
          label="Provider"
          required
          data={(providers || []).map((p) => ({
            value: p.key,
            label: `${CATEGORY_LABEL[p.category] || ""} ${p.name}`,
          }))}
          value={providerKey}
          onChange={setProviderKey}
          searchable
          disabled={!!editing}
        />

        {selectedSpec && (
          <Alert color="blue" icon={<IconAlertCircle size={16} />}>
            <Text size="sm">{selectedSpec.description}</Text>
            {selectedSpec.docs_url && (
              <Text size="xs" mt={4}>
                Dokümantasyon: <a href={selectedSpec.docs_url} target="_blank" rel="noreferrer">{selectedSpec.docs_url}</a>
              </Text>
            )}
          </Alert>
        )}

        <TextInput
          label="Bu credential'ı ne diye adlandıralım?"
          placeholder="Örn: PAX YapıKredi Kasa-1, Iyzico Prod"
          required
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
        />

        <Select
          label="Şube (boş = tüm şubeler)"
          data={[{ value: "", label: "Tüm şubeler" }, ...branches.map((b: any) => ({ value: b.id, label: b.name }))]}
          value={branchId || ""}
          onChange={(v) => setBranchId(v || null)}
          clearable
        />

        {selectedSpec && (
          <Card padding="sm" radius="sm" withBorder bg="gray.0">
            <Stack gap="sm">
              <Text fw={600} size="sm">{selectedSpec.name} — API Alanları</Text>
              {selectedSpec.fields.map(renderField)}
            </Stack>
          </Card>
        )}

        <Group>
          <Switch
            label="Sandbox modu (test)"
            checked={isSandbox}
            onChange={(e) => setIsSandbox(e.currentTarget.checked)}
            description={isSandbox ? "Test ortamı — gerçek para çekilmez" : "⚠️ CANLI — gerçek işlem yapılır"}
          />
          <Switch
            label="Aktif"
            checked={isActive}
            onChange={(e) => setIsActive(e.currentTarget.checked)}
          />
        </Group>

        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={onClose}>İptal</Button>
          <Button onClick={() => mutation.mutate()} loading={mutation.isPending} disabled={!providerKey || !name}>
            {editing ? "Güncelle" : "Oluştur"}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
